import { useNavigate } from 'react-router-dom';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { User, Loader2, MessageSquare, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import type { JobCVCard } from '../types';

interface KanbanCardProps {
  cv: JobCVCard;
  columnId: string | null;
  jobId?: string;
  isSelected?: boolean;
  onToggleSelect?: (cvId: string) => void;
  selectedCVIds?: Set<string>;
  isDraggingOtherSelected?: boolean;
  isDisabled?: boolean;
}

export function KanbanCard({
  cv,
  columnId,
  jobId,
  isSelected,
  onToggleSelect,
  selectedCVIds,
  isDraggingOtherSelected,
  isDisabled = false,
}: KanbanCardProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('ats');

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: cv.id,
    disabled: isDisabled,
    data: {
      cv,
      columnId,
      selectedCVIds: isSelected ? selectedCVIds : undefined,
    },
  });

  const handleNameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Don't navigate if we just finished dragging
    if (isDragging) {
      return;
    }

    // Navigate with job and round context if available
    if (jobId && columnId) {
      // Preserve hideRejected and showOnlyMyPic params from current URL
      const currentParams = new URLSearchParams(window.location.search);
      const hideRejected = currentParams.get('hideRejected');
      const showOnlyMyPic = currentParams.get('showOnlyMyPic');
      const params = new URLSearchParams({
        jobId,
        roundId: columnId,
      });
      if (hideRejected !== null) {
        params.set('hideRejected', hideRejected);
      }
      if (showOnlyMyPic !== null) {
        params.set('showOnlyMyPic', showOnlyMyPic);
      }
      navigate(`/ats/cvs/${cv.id}?${params.toString()}`);
    } else {
      navigate(`/ats/cvs/${cv.id}`);
    }
  };

  // If this card is selected and another selected card is being dragged, show it's part of the group
  const isPartOfMultiDrag = !isDragging && isSelected && isDraggingOtherSelected;

  const style = {
    transform: isDragging ? undefined : CSS.Translate.toString(transform),
    opacity: 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...(!isDisabled ? listeners : {})}
      className={`bg-white rounded-lg border p-3 shadow-sm transition-all relative ${
        isDisabled
          ? 'opacity-50 cursor-default grayscale-[30%]'
          : 'hover:shadow-md cursor-grab active:cursor-grabbing'
      } ${cv.is_rejected ? 'border-red-300 bg-red-50' : 'border-gray-200'} ${
        isDragging ? 'z-50 shadow-lg border-blue-400' : ''
      } ${isSelected ? 'ring-1 ring-blue-500 ring-offset-1' : ''} ${
        isPartOfMultiDrag ? 'scale-98 border-blue-300' : ''
      }`}
    >
      {(isDragging || isPartOfMultiDrag) && (
        <div className="absolute inset-0 bg-white bg-opacity-70 rounded-lg pointer-events-none flex items-center justify-center">
          <div className="absolute inset-0 bg-blue-500 bg-opacity-20 rounded-lg" />
        </div>
      )}
      <div className="flex items-start gap-2 overflow-hidden">
        {/* Avatar + Checkbox column */}
        <div className="flex flex-col items-center gap-1 flex-shrink-0">
          <User className="h-4 w-4 text-gray-400" />
          {onToggleSelect && !isDisabled && (
            <input
              type="checkbox"
              checked={isSelected || false}
              onChange={() => onToggleSelect(cv.id)}
              onPointerDown={(e) => e.stopPropagation()}
              className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500 cursor-pointer"
            />
          )}
        </div>

        {/* CV content */}
        <div className="flex-1 min-w-0">
          {/* CV Name - clickable to navigate */}
          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={handleNameClick}
            className={`text-sm font-medium text-left truncate hover:text-orange-600 hover:underline cursor-pointer ${
              cv.is_rejected ? 'line-through text-gray-500' : 'text-gray-900'
            }`}
          >
            {cv.name || t('kanban.noName')}
          </button>

          {/* Contact info */}
          {(cv.email || cv.phone) && (
            <div className="mt-1 text-xs text-gray-500 space-y-0.5 overflow-hidden">
              {cv.email && <div className="truncate">{cv.email}</div>}
              {cv.phone && <div>{cv.phone}</div>}
            </div>
          )}

          {/* Stats row */}
          <div className="mt-2 flex items-center gap-3 text-xs text-gray-400">
            {cv.note_count > 0 && (
              <span className="flex items-center gap-1">
                <MessageSquare className="h-3 w-3" />
                {cv.note_count}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDistanceToNow(new Date(cv.linked_at), { locale: vi })}
            </span>
          </div>

          {/* Status badge - only show for pending/processing/error */}
          {(cv.processing_status === 'pending' ||
            cv.processing_status === 'processing' ||
            cv.processing_status === 'error') && (
            <div className="mt-2">
              {cv.processing_status === 'error' ? (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-600">
                  {t('kanban.error')}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-600">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {t('kanban.processing')}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
